import {
  clickAddDatasetButton,
  getExploreDatasetPreSelectionStepIds,
  openDatasetExplorer,
  openDatasetExplorerAndSelectDatasetWhenReady,
  openSpatialAnalysis,
  selectOnboardingDataset
} from "@/components/onboarding/utils/exploreDataset";

import { ONBOARDING_MODULE_METADATA } from "./moduleVersions";
import type { ModuleConfig } from "./types";

const dataExploreIntroImageSrc = `${import.meta.env.VITE_BASE_PATH || "/"}images/onboarding_data_explore-1.png`;

// Onboarding config for the Dataset Explorer walkthrough. During the tour, live
// data is replaced by the onboarding-aware wrapper hooks (src/hooks/onboarding/
// explorer/*), which serve the deterministic fixtures in explore-dataset.mocks.ts.
export const exploreDatasetConfig: ModuleConfig = {
  code: ONBOARDING_MODULE_METADATA.exploreDataset.code,
  version: ONBOARDING_MODULE_METADATA.exploreDataset.version,
  intro: {
    title: "Dataset Explorer",
    description:
      "Provide a simple, guided onboarding experience that helps new users understand the core steps in the platform.",
    image: {
      src: dataExploreIntroImageSrc,
      alt: "Dataset Explorer onboarding step preview"
    },
    actions: [
      {
        label: "Let's Start",
        type: "start"
      }
    ],
    // Open the Dataset Explorer behind the intro so the user previews it before
    // the tour starts. Teardown closes it when the intro is dismissed or the
    // tour begins (step-add-dataset re-opens it via its own onActivate).
    onActivate: openDatasetExplorer
  },
  steps: [
    {
      id: "step-dataset-explorer-panel",
      tourId: "dataset-explorer-panel",
      title: "Choose Your Path",
      description:
        "This is where every dataset you bring in lives. The panel keeps your sources, filters, and styling in one place.",
      tooltipConfig: {
        placement: "right"
      },
      // Opens the Dataset Explorer so the tooltip can anchor to add-dataset-btn
      // the moment step-add-dataset activates. onActivate on that step handles
      // cleanup and resume (cross-session re-entry).
      onActivate: openDatasetExplorer,
      onNext: openDatasetExplorer
    },
    {
      id: "step-dataset-card-explorer",
      tourId: "dataset-card-explorer",
      title: "Search and filter",
      description:
        "Search by keyword, then filter by data source, license type, or spatial format to narrow your result.",
      tooltipConfig: {
        placement: "top"
      },
      // Selection lives in DatasetExplorerPopup local state (setSelectedDataset),
      // reachable only through the card's onClick. On Next, click the spotlighted
      // card (data-tourid="dataset-card-explorer") so it becomes selected before
      // the tour advances. goToNext fires onNext while this step's DOM is still
      // mounted.
      onActivate: openDatasetExplorer,
      onNext: selectOnboardingDataset
    },
    {
      id: "step-set-attributes-filter",
      tourId: "dataset-attribute-filter",
      title: "Select & Set Attributes Filter",
      description:
        "Select a dataset and refine it by province, city or district.",
      tooltipConfig: {
        placement: "left"
      },
      // processAddDataSet lives in DatasetRightAttrFilter local scope and is not
      // reachable from config. Mirroring the step-dataset-card-explorer pattern,
      // on Next we click the spotlighted "Add Dataset" button
      // (data-tourid="add-dataset-btn"); its onClick={onAddDataset} invokes
      // processAddDataSet before the tour advances. goToNext fires onNext while
      // this step's DOM is still mounted.
      onActivate: openDatasetExplorerAndSelectDatasetWhenReady
    },
    {
      id: "step-add-dataset",
      tourId: "add-dataset-btn",
      title: "Select & Set Attributes Filter",
      description:
        "Select a dataset and refine it by province, city or district.",
      tooltipConfig: {
        placement: "left"
      },
      // processAddDataSet lives in DatasetRightAttrFilter local scope and is not
      // reachable from config. Mirroring the step-dataset-card-explorer pattern,
      // on Next we click the spotlighted "Add Dataset" button
      // (data-tourid="add-dataset-btn"); its onClick={onAddDataset} invokes
      // processAddDataSet before the tour advances. goToNext fires onNext while
      // this step's DOM is still mounted.
      onActivate: openDatasetExplorerAndSelectDatasetWhenReady,
      onNext: clickAddDatasetButton
    },
    {
      id: "step-dataset-added",
      tourId: "dataset-added-panel",
      title: "Dataset Added",
      description:
        "Your dataset is now loaded. You can add more datasets, custom visualization or remove existing ones from here.",
      // The spatial popup is anchored top-left (absolute top-[30px] left-[16px],
      // width 338px), so place the tooltip to the right to avoid overflowing the
      // left edge of the viewport.
      tooltipConfig: {
        placement: "right-start"
      },
      onActivate: openSpatialAnalysis
    }
  ]
};

/** The step at which the tour selects a dataset (and seeds its filters). */
const SELECTION_STEP_ID = "dataset-attribute-filter";

/**
 * Step ids that come BEFORE the dataset is selected (the panel + card-search
 * steps). While the tour sits on one of these no dataset must be selected, so
 * navigating back from a later step clears the card highlight — and, because
 * the attribute-filter panel is keyed on the selection, its seeded filters too.
 *
 * Derived from the step order (not hand-listed) so it can't drift if steps are
 * reordered; empty if the selection step is ever removed.
 */
export const EXPLORE_DATASET_PRE_SELECTION_STEP_IDS: readonly string[] =
  getExploreDatasetPreSelectionStepIds(
    exploreDatasetConfig.steps,
    SELECTION_STEP_ID
  );
